import { useFeedbackApp } from "../../lib/feedback";
import { Alert, Button, Form, Input, Space } from 'antd'
import { LockOutlined, UserOutlined } from '@ant-design/icons'
import { useEffect, useState } from 'react'
import { useLocation } from 'wouter'
import { useAuth } from '@/auth/AuthContext'
import { BrandLogo } from '@/components/BrandLogo'
import { ApiError } from '@/services/httpClient'
import { normalizeInternalReturnTo } from '@/app/navigation'

type LoginValues = { account: string; credential: string }

export default function LoginPage() {
  const { message } = useFeedbackApp()
  const { user, pending, error, retry, login, requestSMSCode } = useAuth()
  const [, navigate] = useLocation()
  const [form] = Form.useForm<LoginValues>()
  const [submitting, setSubmitting] = useState(false)
  const [codePending, setCodePending] = useState(false)
  const [countdown, setCountdown] = useState(0)

  useEffect(() => {
    if (!countdown) return undefined
    const timer = window.setInterval(() => setCountdown((value) => Math.max(0, value - 1)), 1000)
    return () => window.clearInterval(timer)
  }, [countdown])

  useEffect(() => {
    if (!user) return
    const target = normalizeInternalReturnTo(sessionStorage.getItem('learning_admin_return_to'))
    sessionStorage.removeItem('learning_admin_return_to')
    navigate(target, { replace: true })
  }, [user, navigate])

  if (user) return null

  const submit = async ({ account, credential }: LoginValues) => {
    if (submitting) return
    setSubmitting(true)
    try {
      await login(account, credential)
    } catch (error) { void message.error(error instanceof Error ? error.message : '登录失败') }
    finally { setSubmitting(false) }
  }

  return (
    <main id="main-content" tabIndex={-1} className="login-page">
      <section className="login-panel" aria-labelledby="login-title">
        <div className="login-identity"><BrandLogo /></div>
        <Form<LoginValues> form={form} onValuesChange={(values) => { for (const name of Object.keys(values) as Array<keyof LoginValues>) { if (form.getFieldError(name).length) void form.validateFields([name]).catch(() => undefined) } }} className="login-form" validateTrigger="onBlur" layout="vertical" initialValues={{ account: '', credential: '' }} onFinish={(values) => void submit(values)}>
          <h1 id="login-title">管理员登录</h1>
          <p className="login-description">使用已获授权的手机号登录</p>
          {error ? <Alert type="error" showIcon title="登录状态检查失败" description={error.message} action={<Button onClick={retry}>重新检查</Button>} /> : null}
          <Form.Item label="管理员手机号" name="account" rules={[{ required: true, pattern: /^1\d{10}$/, message: '请输入 11 位中国大陆手机号' }]}>
            <Input prefix={<UserOutlined aria-hidden="true" />} autoComplete="username" inputMode="tel" />
          </Form.Item>
          <Form.Item label="短信验证码" htmlFor="credential" required>
            <Space.Compact block>
              <Form.Item name="credential" noStyle rules={[{ required: true, pattern: /^\d{6}$/, message: '请输入 6 位短信验证码' }]}>
                <Input prefix={<LockOutlined aria-hidden="true" />} autoComplete="one-time-code" inputMode="numeric" maxLength={6} />
              </Form.Item>
              <Form.Item noStyle shouldUpdate>{({ getFieldValue, setFieldValue, validateFields }) => <Button loading={codePending} disabled={submitting || codePending || countdown > 0} onClick={async () => {
                try {
                  await validateFields(['account'])
                  setCodePending(true)
                  const result = await requestSMSCode(String(getFieldValue('account')))
                  if (result.verification_code) setFieldValue('credential', result.verification_code)
                  setCountdown(result.retry_after)
                  void message.success(result.verification_code ? `当前使用固定验证码：${result.verification_code}，已自动填入` : '验证码已发送')
                } catch (error) {
                  if (error instanceof ApiError && error.status === 429) {
                    const remaining = Number(error.details?.retry_after)
                    if (Number.isFinite(remaining) && remaining > 0) setCountdown(Math.ceil(remaining))
                  }
                  if (error instanceof Error) void message.error(error.message)
                } finally {
                  setCodePending(false)
                }
              }}>{countdown ? `${countdown}s 后重试` : '获取验证码'}</Button>}</Form.Item>
            </Space.Compact>
          </Form.Item>
          <Button type="primary" htmlType="submit" block size="large" loading={pending || submitting}>登录</Button>
        </Form>
      </section>
    </main>
  )
}
